import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { useTheme as useNextTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/LanguageContext";
import { cn } from "@/lib/utils";

const THEME_COLORS: Record<string, string> = {
  dark: "#0D1B2A",
  light: "#F5FBF8",
};

interface ThemeToggleProps {
  className?: string;
}

export const ThemeToggle = ({ className }: ThemeToggleProps) => {
  const { resolvedTheme, setTheme } = useNextTheme();
  const { t } = useI18n();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mounted || !resolvedTheme) return;
    const color = THEME_COLORS[resolvedTheme] ?? THEME_COLORS.dark;
    document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"], meta[name="msapplication-TileColor"]').forEach((meta) => {
      meta.content = color;
    });
  }, [mounted, resolvedTheme]);

  const isDark = !mounted || resolvedTheme !== "light";
  const label = isDark ? t.nav.switchToLight : t.nav.switchToDark;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      aria-label={label}
      title={label}
      className={cn("relative h-10 w-10 shrink-0 overflow-hidden rounded-md text-foreground hover:bg-secondary hover:text-highlight", className)}
    >
      <Sun
        className={cn(
          "absolute h-5 w-5 transition-all duration-300 [transition-timing-function:var(--ease-out-quint)]",
          isDark ? "translate-y-6 rotate-90 opacity-0" : "translate-y-0 rotate-0 opacity-100",
        )}
      />
      <Moon
        className={cn(
          "absolute h-5 w-5 transition-all duration-300 [transition-timing-function:var(--ease-out-quint)]",
          isDark ? "translate-y-0 rotate-0 opacity-100" : "-translate-y-6 -rotate-90 opacity-0",
        )}
      />
    </Button>
  );
};
